import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "../lib/api";
import { toast } from "sonner";

export default function AdminInviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPw) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await client.post("/admin/roster/accept", {
        invite_token: token,
        full_name: fullName,
        company_name: companyName || "FreightPilotAI",
        password,
      });
      toast.success("Account created! Sign in with your new credentials.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to accept invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4" data-testid="admin-invite-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-2">
            Admin invitation
          </div>
          <h1 className="text-2xl font-semibold text-white">Set up your admin account</h1>
          <p className="text-sm text-neutral-500 mt-1">
            You've been invited as an admin on FreightPilot AI.
          </p>
        </div>

        <form onSubmit={submit} className="card-surface p-6 rounded-lg border border-white/10 space-y-4">
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Your name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              data-testid="invite-full-name"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Company (optional)</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="FreightPilotAI"
              data-testid="invite-company"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Choose password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              data-testid="invite-password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
            <div className="text-[11px] text-neutral-600 mt-1">Minimum 8 characters.</div>
          </div>
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Confirm password</label>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              data-testid="invite-confirm-password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !fullName || !password || !confirmPw}
            data-testid="invite-submit"
            className="w-full rounded-full py-2.5 text-sm font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create admin account"}
          </button>
        </form>
      </div>
    </div>
  );
}
